import type { Program } from '@vta/types';

/**
 * Program → display metadata.
 *
 * a11y rule (PRD §9.4): color is never the sole differentiator. Each entry
 * pairs the token-driven hue with a unique glyph, so a user with monochrome
 * vision can still distinguish programs from the legend and from individual
 * markers.
 *
 * The glyphs are intentionally text characters (not lucide icons) so they
 * render inside the small marker DOM nodes without dragging an icon import
 * into the marker render path. Keep them ≤ 1 character each.
 */
export interface ProgramLegendEntry {
  program: Program;
  label: string;
  glyph: string;
  /** Tailwind utility for the marker dot. Tokens-driven, no raw hex. */
  swatchClass: string;
  /** Hex color for the three.js side (clusters need raw color). */
  cssVar: string;
}

export const programLegend: ProgramLegendEntry[] = [
  {
    program: 'v5rc',
    label: 'V5RC',
    glyph: '◆',
    swatchClass: 'bg-program-v5rc',
    cssVar: 'rgb(var(--program-v5rc))',
  },
  {
    program: 'viqrc',
    label: 'VIQRC',
    glyph: '●',
    swatchClass: 'bg-program-viqrc',
    cssVar: 'rgb(var(--program-viqrc))',
  },
  {
    program: 'vurc',
    label: 'VURC',
    glyph: '▲',
    swatchClass: 'bg-program-vurc',
    cssVar: 'rgb(var(--program-vurc))',
  },
  {
    program: 'vairc',
    label: 'VAIRC',
    glyph: '◼',
    swatchClass: 'bg-program-vairc',
    cssVar: 'rgb(var(--program-vairc))',
  },
  {
    program: 'unknown',
    label: 'Other',
    glyph: '○',
    swatchClass: 'bg-fg-faint',
    cssVar: 'rgb(var(--fg-subtle))',
  },
];

const PROGRAM_BY_KEY: Record<Program, ProgramLegendEntry> = programLegend.reduce(
  (acc, entry) => {
    acc[entry.program] = entry;
    return acc;
  },
  {} as Record<Program, ProgramLegendEntry>,
);

export function programMeta(program: Program | string | null | undefined): ProgramLegendEntry {
  if (!program) return PROGRAM_BY_KEY.unknown;
  return PROGRAM_BY_KEY[program as Program] ?? PROGRAM_BY_KEY.unknown;
}

/**
 * Floating legend chip strip rendered over the globe.
 *
 * Positioned by the parent (`GlobeScene` mounts it at top-right). Kept as a
 * named export rather than the default so consumers can also import the
 * raw `programLegend` array for swatches in other surfaces.
 */
export function ProgramLegend({ className }: { className?: string }) {
  return (
    <div
      className={[
        'pointer-events-none flex flex-wrap items-center gap-1.5 rounded-md border border-border-subtle bg-bg-raised/85 px-2 py-1.5 text-2xs uppercase tracking-[0.18em] text-fg-muted shadow-rest backdrop-blur-md',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="Program legend"
    >
      {programLegend.map((entry) => (
        <span key={entry.program} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className={`inline-flex h-3 w-3 items-center justify-center rounded-full text-[10px] leading-none text-bg-base ${entry.swatchClass}`}
          >
            {entry.glyph}
          </span>
          <span>{entry.label}</span>
        </span>
      ))}
    </div>
  );
}
